import * as couponService from '@cart-services/core/couponService'
import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { getDisplayAttributes } from '@cart-services/parser/parseAttributes'
import { parserFastpassOrderItemBySkuMap } from '@cart-services/parser/parserFastpassOrderItemBySkuMap'
import { type CartItem } from '@cart-services/types'
import { CART_FORM_ERROR_MESSAGE } from '@cart-services/zod-schema/cartFormSchema'
import { CartListItem } from '@cart-ui/CartListItem/CartListItem'
import { SkeletonCartListItem } from '@cart-ui/CartListItem/SkeletonCartListItem'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { CouponType } from '@hooks-api/types/dataModel/coupon'
import * as stylex from '@stylexjs/stylex'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { useStmCart } from '../../CartProvider'

const styles = stylex.create({
  selectVariantRootStyle: {
    minWidth: {
      'default': 180,
      '@media (max-width: 768px)': 160,
    },
    width: 'fit-content',
  },
})

export interface CartListItemControllerProps {
  item: CartItem
  index: number
}

export const CartListItemController = (props: CartListItemControllerProps) => {
  const { item, index } = props
  const { campaignId, currency, projectCode, countryCode, campaignCode, platform } = useStmCart()
  const { calculateResult } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const { upsertCartItem, deleteCartItem, resetCartValues, items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { getMergedCouponCriterions } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const orderItemBySkuMap = useMemo(() => parserFastpassOrderItemBySkuMap(calculateResult?.order_items), [calculateResult])

  const handleQuantityChange = useCallbackEvent(
    (cartItemId: CartItem['cartItemId']) => (value?: number) => {
      if (value === undefined || value === 0) return
      upsertCartItem({ cartItemId, quantity: value })
    },
  )

  const handleDelete = useCallbackEvent(
    (cartItemId: CartItem['cartItemId']) => () => {
      if (cartItems.length > 1) {
        deleteCartItem({ cartItemId })
      }
      else {
        resetCartValues()
      }
    },
  )

  const { productMap } = useFpB2bProductsInfo({ projectCode, countryCode, campaignCode, campaignId, platform })
  const product = useMemo(() => productMap?.[item.cartItemId], [productMap, item.cartItemId])
  const orderItemFromCalculate = useMemo(() => orderItemBySkuMap[item.sku], [orderItemBySkuMap, item.sku])

  const couponCriterions = useMemo(() => getMergedCouponCriterions(
    orderItemFromCalculate?.applied_coupon_ids ?? [],
    [CouponType.SPECIFIC_PRODUCT, CouponType.WAREHOUSE_PRODUCT],
  ), [getMergedCouponCriterions, orderItemFromCalculate?.applied_coupon_ids])

  const {
    stock,
    salePrice,
    productTitle,
    originalPrice,
    productPageUrl,
    maxQuantity,
  } = useMemo(() => couponService.getCartListItemProps(item, product, orderItemFromCalculate), [item, product, orderItemFromCalculate])
  const attributes = getDisplayAttributes(product)

  const productImgSrc = product?.parent_last_gallery_image || product?.thumbnail || ''

  if (!product) return <SkeletonCartListItem />

  return (
    <Controller
      name={`cartItems.${index}`}
      render={({ field: { ref }, fieldState: { error } }) => {
        const hasError = !!error
        const isAdjustedToMaxQuantity = error?.message === CART_FORM_ERROR_MESSAGE.ADJUST_TO_MAX_QUANTITY
        return (
          <CartListItem
            showUnitPrice
            selectVariantRootStyle={styles.selectVariantRootStyle}
            rootRef={fieldRefHelper(ref)}
            quantity={item.quantity}
            maxQuantity={maxQuantity}
            productTitle={productTitle}
            productImgSrc={productImgSrc}
            productImgWidth={60}
            originalPrice={originalPrice}
            salePrice={salePrice}
            couponCriterions={couponCriterions}
            stock={stock}
            isAddonV2={item.is_addon_v2}
            isAdjustedToMaxQuantity={isAdjustedToMaxQuantity}
            errorHighlight={hasError && !isAdjustedToMaxQuantity}
            onDelete={handleDelete(item.cartItemId)}
            onQuantityChange={handleQuantityChange(item.cartItemId)}
            currency={currency}
            attributes={attributes}
            productPageUrl={productPageUrl}
            preOrderInfo={product.pre_order_info}
          />
        )
      }}
    />
  )
}
